import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart"
import { Skeleton } from "@/components/ui/skeleton"
import type { ReportCustomerRow } from "@/lib/endpoints"
import { formatRupees } from "@/lib/money"
import { routes } from "@/routes/paths"

/**
 * Top customers by value — where the money came from in the chosen period.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * A HORIZONTAL BAR, AND NOT A PIE. Customer names are long, Devanagari names
 * longer, and a pie puts them in a legend the eye has to match back to a wedge;
 * beyond four slices the small ones are unreadable and the ordering is guesswork.
 * A horizontal bar sorted largest-first says "where the money comes from" and
 * "how concentrated it is" in one glance, and the labels sit beside the marks
 * rather than in a key. There is deliberately NO pie chart on this screen.
 *
 * IT IS THE NET FIGURE (§17.3). `netBilledPaise` is what the customer was billed
 * LESS the credit notes raised against him in this period — the same figure the
 * table below and the card above use, because a chart drawn from the gross
 * column would rank a customer by bills that were partly reversed.
 *
 * A BAR CAN GO LEFT, AND THAT IS CORRECT RATHER THAN A BUG TO FLOOR AWAY. A
 * period in which one buyer was credited more than he was billed genuinely
 * reduced the year's liability, and hiding it would make the chart disagree with
 * the total under the table. Credit notes count in the month they were ISSUED,
 * even against an older bill, which is what makes that case ordinary rather than
 * exotic.
 *
 * TOP {@link MAX_BARS} ONLY, AND THE TABLE BELOW CARRIES EVERYONE. Beyond about
 * eight bars the labels collide and the chart stops answering the question it
 * exists for. The rows arrive from the server already sorted largest-first, so
 * this slices rather than sorts — one definition of "biggest customer", on the
 * server, shared with the table.
 * ═════════════════════════════════════════════════════════════════════════════
 */

const MAX_BARS = 8

/** The longest name an axis tick can carry before it eats the plot. */
const LABEL_CHARS = 16

interface Column {
  customerId: number
  /** Truncated for the axis; the buttons below carry the full name. */
  label: string
  fullName: string
  net: number
  invoiceCount: number
}

export function ChartTopCustomers({
  rows,
  loading,
  from,
  to,
}: {
  /** `ReportCustomersResponse.data` — ALREADY SORTED by the server. */
  rows: ReportCustomerRow[] | undefined
  loading: boolean
  /** The period's INCLUSIVE days, as the server resolved them — for the links. */
  from: string
  to: string
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const columns = useMemo<Column[]>(() => {
    if (rows === undefined) return []

    return rows.slice(0, MAX_BARS).map((row) => {
      const fullName = row.customerName ?? t("lbl.customerUnknown")
      return {
        customerId: row.customerId,
        fullName,
        label: fullName.length > LABEL_CHARS ? `${fullName.slice(0, LABEL_CHARS - 1)}…` : fullName,
        /*
         * PAISE, straight from the server. Recharts only compares magnitudes to
         * scale the plot; the division that would turn this into a float never
         * happens, and `formatRupees` is the only thing that ever renders it.
         */
        net: row.netBilledPaise,
        invoiceCount: row.invoiceCount,
      }
    })
  }, [rows, t])

  const chartConfig = {
    net: { label: t("reports.chart.billedTo"), color: "var(--primary)" },
  } satisfies ChartConfig

  const openCustomer = (column: Column | undefined) => {
    if (column === undefined) return
    navigate(routes.invoicesForCustomerInPeriod(column.customerId, from, to))
  }

  // Loaded, and nobody was billed in this period. The empty state belongs to the
  // table below, which says it in a sentence; a chart frame with no bars would
  // say it twice and less clearly.
  if (!loading && rows !== undefined && columns.length === 0) return null

  return (
    <Card className="@container/card">
      <CardHeader>
        <CardTitle>{t("reports.chart.topTitle")}</CardTitle>
        <CardDescription>{t("reports.chart.topSubtitle")}</CardDescription>
      </CardHeader>

      <CardContent className="px-2 pt-4 sm:px-6 sm:pt-6">
        {loading || rows === undefined ? (
          <Skeleton className="h-[240px] w-full" />
        ) : (
          <>
            <ChartContainer config={chartConfig} className="aspect-auto h-[240px] w-full">
              <BarChart
                accessibilityLayer
                layout="vertical"
                data={columns}
                margin={{ left: 4, right: 12 }}
                onClick={(state) => {
                  const index = state?.activeTooltipIndex
                  if (typeof index === "number") openCustomer(columns[index])
                }}
                className="cursor-pointer"
              >
                <CartesianGrid horizontal={false} />

                {/*
                  * THE VALUE AXIS IS HIDDEN, exactly as the other two charts on
                  * this screen hide theirs. Rupee ticks would mean either a
                  * repeated figure nobody asked for or an abbreviation like
                  * "12.8L" — and abbreviating means a rupee formatter outside
                  * `packages/tax`, the second formatter CLAUDE.md exists to
                  * prevent. The CATEGORY axis carries names, which are text, so
                  * it stays.
                  */}
                <XAxis type="number" dataKey="net" hide />
                <YAxis
                  type="category"
                  dataKey="label"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={6}
                  width={110}
                />

                <ChartTooltip
                  cursor={false}
                  content={
                    <ChartTooltipContent
                      indicator="dot"
                      labelFormatter={(_label, payload) => {
                        const column = payload?.[0]?.payload as Column | undefined
                        if (column === undefined) return ""
                        return `${column.fullName} · ${t("reports.customerBills", {
                          count: column.invoiceCount,
                        })}`
                      }}
                      formatter={(value) => (
                        <span className="font-medium tabular-nums">
                          {formatRupees(Number(value))}
                        </span>
                      )}
                    />
                  }
                />

                <Bar dataKey="net" fill="var(--color-net)" radius={4} />
              </BarChart>
            </ChartContainer>

            {/*
              * THE ACCESSIBLE PRIMARY. An SVG rectangle is not reachable by
              * keyboard or screen reader, and the axis tick beside it is a
              * truncated name. These rows carry the FULL name and the exact
              * figure, and they are the tap target on a phone (§11.1, §11.4).
              */}
            <ul className="mt-4 flex flex-col divide-y" aria-label={t("reports.chart.topTitle")}>
              {columns.map((column) => (
                <li key={column.customerId}>
                  <button
                    type="button"
                    onClick={() => openCustomer(column)}
                    className="hover:bg-accent/50 focus-visible:ring-ring flex min-h-11 w-full items-center gap-3 rounded-md px-2 py-2 text-left transition-colors focus-visible:ring-2 focus-visible:outline-none"
                  >
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate text-sm font-medium">{column.fullName}</span>
                      <span className="text-muted-foreground text-xs">
                        {t("reports.customerBills", { count: column.invoiceCount })}
                      </span>
                    </span>
                    <span className="shrink-0 text-sm font-medium tabular-nums">
                      {formatRupees(column.net)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </CardContent>
    </Card>
  )
}
